import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RefundConfirmationModal } from '../common';

const EnhancedRefundModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  dealId, 
  dealValue = 0, 
  dealCurrency = 'USD',
  courseId = null
}) => {
  const [refundAmount, setRefundAmount] = useState('');
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [reasonDetails, setReasonDetails] = useState('');
  const [lessonsCompleted, setLessonsCompleted] = useState('');
  const [tutors, setTutors] = useState([]);
  const [tutorId, setTutorId] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const standardReasons = [
    'Customer Dissatisfaction',
    'Course Not as Described',
    'Personal Circumstances',
    'Health Issues',
    'Technical Problems',
    'Schedule Conflicts',
    'Course Too Difficult',
    'Course Too Basic',
    'Change of Mind',
    'Other'
  ];
  
  // Clear form when modal opens/closes or deal changes
  useEffect(() => {
    setRefundAmount('');
    setReason('');
    setCustomReason('');
    setReasonDetails('');
    setLessonsCompleted('');
    setTutorId('');
    setError(null);
    
    // Fetch tutors when modal opens
    if (isOpen) {
      fetchTutors();
    }
  }, [isOpen, dealId]);
  
  // Fetch tutors for dropdown
  const fetchTutors = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      // Fetch users with tutor role
      const response = await axios.get('/api/employees/tutors', config);
      setTutors(response.data);
      
      // If this deal has a course, try to pre-select the tutor
      if (courseId) {
        try {
          const courseResponse = await axios.get(`/api/settings/courses/${courseId}`, config);
          if (courseResponse.data && courseResponse.data.tutor_id) {
            setTutorId(courseResponse.data.tutor_id);
          }
        } catch (err) {
          console.error('Error fetching course details:', err);
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching tutors:', err);
      setError('Failed to load tutors: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };
  
  // Handle Escape key to close
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen && !showConfirmation) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, showConfirmation]);
  
  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!refundAmount) {
      setError('Please enter a refund amount');
      return;
    }
    
    if (!reason && (!customReason || reason !== 'Other')) {
      setError('Please select or enter a reason for the refund');
      return;
    }
    
    // Clear any previous errors
    setError(null);
    
    // Show confirmation dialog
    setShowConfirmation(true);
  };
  
  const executeRefund = async () => {
    // Close confirmation modal first
    setShowConfirmation(false);
    
    try {
      setLoading(true);
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }
      
      // Parse the string value to float, or default to 0 if invalid
      const amount = refundAmount === '' ? 0 : parseFloat(refundAmount);
      
      // Determine the final reason to use
      const finalReason = reason === 'Other' ? customReason : reason;
      
      // Create refund data object
      const refundData = {
        deal_id: dealId,
        amount: amount,
        reason: finalReason,
        reason_details: reasonDetails,
        lessons_completed: lessonsCompleted === '' ? null : parseInt(lessonsCompleted),
        tutor_id: tutorId === '' ? null : parseInt(tutorId)
      };
      
      // Make API call to create refund record
      const config = {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      };
      
      await axios.post('/api/refunds', refundData, config);
      
      // Call the onConfirm callback with the refund amount
      onConfirm(amount);
      setLoading(false);
      
    } catch (error) {
      console.error('Error processing refund:', error);
      setError('Failed to process refund: ' + (error.response?.data?.message || error.message));
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  // Format the currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: dealCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  
  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4"
        onClick={handleBackdropClick}
      >
        <div className="bg-white rounded-lg w-full max-w-xl">
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="text-lg font-semibold">Process Refund</h2>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {loading ? (
            <div className="p-6 text-center">
              <div className="mb-2">Processing...</div>
              <div className="w-8 h-8 border-t-4 border-b-4 border-blue-500 rounded-full animate-spin mx-auto"></div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-800 rounded-lg">
                  {error}
                </div>
              )}
              
              <div className="mb-5">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Refund Amount ({dealCurrency})
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder={`Enter amount (max: ${formatCurrency(dealValue)})`}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  autoFocus
                  required
                />
                <p className="text-sm text-gray-600 mt-1">
                  Original deal amount: {formatCurrency(dealValue)}
                </p>
                {refundAmount !== '' && parseFloat(refundAmount) > dealValue && (
                  <p className="text-sm text-red-600 mt-1">
                    Warning: Refund amount exceeds original deal value
                  </p>
                )}
              </div>
              
              <div className="mb-5">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Reason for Refund
                </label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-2"
                  required
                >
                  <option value="">Select a reason</option>
                  {standardReasons.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                
                {reason === 'Other' && (
                  <input
                    type="text"
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder="Please specify the reason"
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  />
                )}
              </div>
              
              <div className="mb-5">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Additional Details
                </label>
                <textarea
                  value={reasonDetails}
                  onChange={(e) => setReasonDetails(e.target.value)}
                  placeholder="Enter any additional details about this refund (optional)"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Lessons Completed
                  </label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={lessonsCompleted}
                    onChange={(e) => setLessonsCompleted(e.target.value)}
                    placeholder="Number of lessons"
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Tutor
                  </label>
                  <select
                    value={tutorId}
                    onChange={(e) => setTutorId(e.target.value)}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  >
                    <option value="">Select a tutor</option>
                    {tutors.map((tutor) => (
                      <option key={tutor.id} value={tutor.id}>{tutor.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md shadow-sm hover:bg-yellow-700"
                >
                  Process Refund
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      
      {/* Confirmation Modal */}
      <RefundConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={executeRefund}
        amount={refundAmount}
        currency={dealCurrency}
        message={`Are you sure you want to process a refund of ${formatCurrency(refundAmount)}? This action cannot be undone.`}
      />
    </>
  );
};

export default EnhancedRefundModal;